import React, { useEffect, useState } from "react";
import { Alert, Button, Paper, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import SegmentPicker from "./SegmentPicker";
import SegmentEnrollment from "./SegmentEnrollment";
import { read, act, localId } from "./api";

export default function WorkflowSegments({ api, workflow, versionId }) {
  const [selected, setSelected] = useState([]);
  const [excluded, setExcluded] = useState([]);
  const [stopped, setStopped] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const id = localId(workflow.id);
  useEffect(() => {
    let active = true;
    read(api, "workflows")
      .then(async (items) => {
        const item = items.find((row) => row.id === id);
        const rows = await Promise.all(
          (item?.excludedSegmentIds || []).map((id) =>
            read(api, "segment", { id }),
          ),
        );
        if (active) setExcluded(rows);
        const result = await read(api, "excluded_enrollments", { id });
        if (active) setStopped(result.items);
      })
      .catch((err) => active && setError(err.message));
    return () => {
      active = false;
    };
  }, [api, id]);
  const kind = workflow.recipientKind;
  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Enroll segments</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <SegmentPicker
          api={api}
          kind={kind}
          value={selected}
          onChange={setSelected}
        />
        <Button
          disabled={!selected.length || !versionId}
          onClick={() => setOpen(true)}
        >
          Preview segment enrollment
        </Button>
        <Typography variant="h6">Excluded segments</Typography>
        <Typography>
          Exclusions apply to all versions, override other selections and stop
          existing enrollments. Removing an exclusion does not restart anyone.
        </Typography>
        <SegmentPicker
          api={api}
          kind={kind}
          value={excluded}
          onChange={setExcluded}
        />
        <Button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await act(api, "exclusions", {
                workflowId: id,
                segmentIds: excluded.map((row) => row.id),
              });
              enqueueSnackbar(
                "Exclusions saved; affected enrollments will be stopped.",
                { variant: "success" },
              );
            } catch (err) {
              enqueueSnackbar(err.message, { variant: "error" });
            } finally {
              setBusy(false);
            }
          }}
        >
          Save exclusions
        </Button>
        {stopped.map((row) => (
          <Stack key={row.id} direction="row" spacing={1}>
            <Typography>{row.email} — stopped by exclusion</Typography>
            <Button
              disabled={busy || !versionId}
              onClick={async () => {
                if (
                  !window.confirm(
                    "Start a new enrollment at the entry stage of the selected live version? The welcome email may be sent again.",
                  )
                )
                  return;
                setBusy(true);
                try {
                  await act(api, "reenroll", {
                    id: row.id,
                    workflowId: id,
                    versionId: localId(versionId),
                  });
                  setStopped((items) =>
                    items.filter((item) => item.id !== row.id),
                  );
                  enqueueSnackbar("New enrollment created.", {
                    variant: "success",
                  });
                } catch (err) {
                  enqueueSnackbar(err.message, { variant: "error" });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Re-enroll deliberately
            </Button>
          </Stack>
        ))}
        {open && (
          <SegmentEnrollment
            api={api}
            segments={selected}
            workflowId={id}
            versionId={localId(versionId)}
            onClose={() => setOpen(false)}
          />
        )}
      </Stack>
    </Paper>
  );
}
